// src/services/qbankApi.ts
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { getQbankAccessToken } from '@/lib/logto';
import type { Wallet } from '@/types/qbank';

/**
 * QBank API client (SPIKE, read-only). Talks to qbank at `/wallets/*`.
 *
 * Auth: a Bearer token from getQbankAccessToken(). In dev that's QBank's
 * internal test-OIDC token (Logto bypassed) — see src/lib/logto.ts.
 *
 * Response quirk: QBank returns `Ok(JsonSerializer.Serialize(...))` — a JSON
 * STRING sent as text/plain (no `{ data }` envelope). We read it as text and
 * JSON.parse (twice if double-encoded), tolerating plain-text error bodies.
 *
 * Dev: env.qbankApiUrl = '/qbank-api' (Vite proxy → local QBank :8080).
 * Prod: env.qbankApiUrl = 'https://qbank.qlash.gg'.
 */
async function parseQbankBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    let v: unknown = JSON.parse(text);
    if (typeof v === 'string') v = JSON.parse(v);
    return v;
  } catch {
    return text; // plain-text error body (e.g. "User not authorized")
  }
}

export const qbankApi = createApi({
  reducerPath: 'qbankApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: env.qbankApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getQbankAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        headers.set('x-platform-id', env.platformId);
        headers.set('Accept', 'application/json');
        return headers;
      },
    }),
    { maxRetries: 1 },
  ),
  keepUnusedDataFor: 120,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    // GET /wallets/get (no currencyId) → ALL of the caller's wallets. `sub` is
    // taken from the JWT, never sent as a param.
    getWallets: builder.query<Wallet[], void>({
      query: () => ({
        url: '/wallets/get',
        responseHandler: parseQbankBody,
      }),
      transformResponse: (raw: unknown): Wallet[] => (Array.isArray(raw) ? (raw as Wallet[]) : []),
    }),
  }),
});

export const { useGetWalletsQuery } = qbankApi;
